'use no memo';
import type {ReactElement} from 'react';
import {useAppSelector} from '@/hooks';
import type {CustomItemControlsWrapperProps} from '@Components/poster-editor/components/custom-item-controls/components/custom-item-controls-wrapper/custom-item-controls-wrapper.types';
import type {ItemType} from '@PosterWhiteboard/items/item/item.types';
import {ItemLoadingIndicator} from './item-loading-indicator';
import {LOADING_INDICATOR_FONT_SIZE} from './item-loading-indicators.types';
import {useMemo} from 'react';

export function ItemLoadingIndicators({pageHashedId}: CustomItemControlsWrapperProps): ReactElement<any> | null {
  const posterScale = useAppSelector((state) => {
    return state.posterEditor.posterObject?.scaling.scale;
  });
  const activeItemIds = useAppSelector((state) => {
    return state.posterEditor.activeItemIds[pageHashedId];
  });
  const itemLoadingStates = useAppSelector((state) => {
    return state.posterEditor.itemLoadingStates[pageHashedId];
  });

  const textMeasurementCanvas = useMemo(() => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.font = `${LOADING_INDICATOR_FONT_SIZE}px "Figtree", Helvetica, Arial, sans-serif`;
    }
    return ctx;
  }, []);

  const page = window.posterEditor?.whiteboard?.pages?.getPage(pageHashedId);
  if (!page || !posterScale) {
    return null;
  }

  if (!activeItemIds || activeItemIds.length === 0) {
    return null;
  }

  const fitToScreenScale = page.poster.scaling.getFitToScreenScale();

  const getSelectedItemsWithLoading = (): ItemType[] => {
    const items: ItemType[] = [];

    for (const itemId of activeItemIds) {
      const item = page.items.getItem(itemId);
      if (!item?.fabricObject) {
        continue;
      }

      const hasLoadingState = itemLoadingStates?.[item.uid]?.isVisible;
      if (hasLoadingState) {
        items.push(item);
      }

      if (item.isSlideshow()) {
        const selectedSlide = item.getSelectedSlide();
        if (selectedSlide?.fabricObject) {
          const slideHasLoadingState = itemLoadingStates?.[selectedSlide.uid]?.isVisible;
          if (slideHasLoadingState) {
            items.push(selectedSlide);
          }
        }
      }
    }

    return items;
  };

  const selectedItems = getSelectedItemsWithLoading();

  const getLoadingIndicatorForItems = (): ReactElement<any>[] => {
    return selectedItems
      .map((item) => {
        const loadingState = itemLoadingStates?.[item.uid];
        if (!loadingState) {
          return null;
        }

        return (
          <ItemLoadingIndicator
            key={item.uid}
            item={item}
            loadingState={loadingState}
            posterScale={posterScale}
            fitToScreenScale={fitToScreenScale}
            textMeasurementCanvas={textMeasurementCanvas}
          />
        );
      })
      .filter((element): element is ReactElement<any> => element !== null);
  };

  return <>{getLoadingIndicatorForItems()}</>;
}
